// js/data.js — Supabase backend (replaces localStorage)

const _SUPA_URL = 'https://ugdgqahwjlkhsarcqrko.supabase.co';
const _SUPA_KEY = 'sb_publishable_qsuiA7CgTbi4vXn5KJqYgg_fAglhgy8';

// supabase global is loaded by CDN before bundle.js
const _sb = supabase.createClient(_SUPA_URL, _SUPA_KEY);

// ── Utilities ────────────────────────────────────────────────────────────────

/** Compute decimal hours between two "HH:MM" strings. Returns 0 if invalid. */
export function computeDuration(startTime, endTime) {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? Math.round(diff / 60 * 100) / 100 : 0;
}

function currentUser() {
  return localStorage.getItem('tl_username') || 'Unknown';
}

// Row mappers — entries use snake_case in DB, camelCase in JS
function entryFromRow(row) {
  return {
    id:          row.id,
    userName:    row.user_name,
    projectId:   row.project_id,
    categoryId:  row.category_id,
    date:        row.date,
    startTime:   row.start_time,
    endTime:     row.end_time,
    duration:    row.duration,
    description: row.description || '',
  };
}

function entryToRow(entry, overrideUser) {
  const row = {
    user_name:   overrideUser || currentUser(),
    project_id:  entry.projectId || null,
    category_id: entry.categoryId,
    date:        entry.date,
    start_time:  entry.startTime,
    end_time:    entry.endTime,
    duration:    entry.duration,
    description: entry.description || '',
  };
  if (entry.id) row.id = entry.id;
  return row;
}

// ── Seed data ────────────────────────────────────────────────────────────────

const DEFAULT_CATEGORIES = [
  { name: 'Project Work',     type: 'work',    color: '#3b82f6' },
  { name: 'Internal Meeting', type: 'work',    color: '#8b5cf6' },
  { name: 'Training',         type: 'work',    color: '#06b6d4' },
  { name: 'Sick Leave',       type: 'leave',   color: '#f59e0b' },
  { name: 'Vacation',         type: 'leave',   color: '#10b981' },
  { name: 'Holiday',          type: 'holiday', color: '#ef4444' },
];

const DEFAULT_PROJECTS = [
  { name: 'TimeLogger',           active: true, billable: false },
  { name: 'Admin',                active: true, billable: false },
  { name: 'Business Development', active: true, billable: true  },
  { name: 'R&D',                  active: true, billable: true  },
];

export async function seedIfEmpty() {
  const { count } = await _sb
    .from('categories')
    .select('*', { count: 'exact', head: true });
  if (count === 0) {
    await _sb.from('categories').insert(DEFAULT_CATEGORIES);
    await _sb.from('projects').insert(DEFAULT_PROJECTS);
  }
}

// ── Norwegian Public Holidays ─────────────────────────────────────────────────

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day   = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function localISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Returns { 'YYYY-MM-DD': 'Holiday name' } for all Norwegian public holidays in year. */
export function getNorwegianHolidays(year) {
  const e = easterSunday(year);
  return {
    [localISO(new Date(year, 0, 1))]:   'Nyttårsdag',
    [localISO(addDays(e, -3))]:          'Skjærtorsdag',
    [localISO(addDays(e, -2))]:          'Langfredag',
    [localISO(e)]:                       '1. påskedag',
    [localISO(addDays(e, 1))]:           '2. påskedag',
    [localISO(new Date(year, 4, 1))]:    'Arbeidernes dag',
    [localISO(new Date(year, 4, 17))]:   'Grunnlovsdagen',
    [localISO(addDays(e, 39))]:          'Kristi himmelfartsdag',
    [localISO(addDays(e, 49))]:          '1. pinsedag',
    [localISO(addDays(e, 50))]:          '2. pinsedag',
    [localISO(new Date(year, 11, 25))]:  '1. juledag',
    [localISO(new Date(year, 11, 26))]:  '2. juledag',
  };
}

/**
 * Seeds Norwegian holiday entries for `year` into Supabase if not already done.
 * Uses a localStorage flag to avoid repeated Supabase checks.
 */
export async function seedHolidaysForYear(year) {
  const flagKey = `tl_holidays_seeded_${year}`;
  if (localStorage.getItem(flagKey)) return;

  const { data: cats } = await _sb
    .from('categories')
    .select('id')
    .eq('type', 'holiday')
    .limit(1);
  const holidayCat = cats?.[0];
  if (!holidayCat) return;

  const { count } = await _sb
    .from('entries')
    .select('*', { count: 'exact', head: true })
    .eq('category_id', holidayCat.id)
    .gte('date', `${year}-01-01`)
    .lte('date', `${year}-12-31`);

  if (count > 0) {
    localStorage.setItem(flagKey, '1');
    return;
  }

  const holidays = getNorwegianHolidays(year);
  const rows = Object.entries(holidays).map(([date, name]) => ({
    user_name:   'system',
    project_id:  null,
    category_id: holidayCat.id,
    date,
    start_time:  '00:00',
    end_time:    '00:00',
    duration:    0,
    description: name,
  }));
  await _sb.from('entries').insert(rows);
  localStorage.setItem(flagKey, '1');
}

// ── Custom Holidays ───────────────────────────────────────────────────────────

export async function getCustomHolidays() {
  const { data } = await _sb.from('holidays').select('*').order('date');
  return data || [];
}

export async function saveCustomHoliday(holiday) {
  const { data: cats } = await _sb
    .from('categories').select('id').eq('type', 'holiday').limit(1);
  const holidayCat = cats?.[0];

  if (holiday.id) {
    await _sb.from('holidays')
      .update({ date: holiday.date, name: holiday.name })
      .eq('id', holiday.id);
    return holiday;
  }

  const { data: h } = await _sb
    .from('holidays')
    .insert({ date: holiday.date, name: holiday.name, type: 'custom' })
    .select()
    .single();

  if (holidayCat) {
    const { count } = await _sb
      .from('entries')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', holidayCat.id)
      .eq('date', holiday.date);
    if (count === 0) {
      await _sb.from('entries').insert({
        user_name:   currentUser(),
        project_id:  null,
        category_id: holidayCat.id,
        date:        holiday.date,
        start_time:  '08:00',
        end_time:    '16:00',
        duration:    8,
        description: holiday.name,
      });
    }
  }
  return h;
}

export async function deleteCustomHoliday(id) {
  const { data: h } = await _sb.from('holidays').select('*').eq('id', id).single();
  if (!h) return;
  await _sb.from('holidays').delete().eq('id', id);
  const { data: cats } = await _sb
    .from('categories').select('id').eq('type', 'holiday').limit(1);
  const holidayCat = cats?.[0];
  if (holidayCat) {
    await _sb.from('entries')
      .delete()
      .eq('category_id', holidayCat.id)
      .eq('date', h.date)
      .eq('description', h.name);
  }
}

/**
 * Returns merged { 'YYYY-MM-DD': name } combining Norwegian + custom holidays for year.
 */
export async function getAllHolidays(year) {
  const national = getNorwegianHolidays(year);
  const customs  = await getCustomHolidays();
  const customMap = customs
    .filter(h => h.date.startsWith(`${year}-`))
    .reduce((acc, h) => { acc[h.date] = h.name; return acc; }, {});
  return { ...national, ...customMap };
}

// ── Entries ───────────────────────────────────────────────────────────────────

export async function getEntries(fromDate = null, toDate = null) {
  let q = _sb.from('entries').select('*').order('date').order('start_time');
  if (fromDate) q = q.gte('date', fromDate);
  if (toDate)   q = q.lte('date', toDate);
  const { data } = await q;
  return (data || []).map(entryFromRow);
}

/** Insert (no id) or update (has id) an entry. Returns the saved entry. */
export async function saveEntry(entry) {
  const duration = computeDuration(entry.startTime, entry.endTime);
  const row = entryToRow({ ...entry, duration });
  if (!entry.id) {
    const { data, error } = await _sb.from('entries').insert(row).select().single();
    if (error) throw new Error(error.message);
    return entryFromRow(data);
  }
  const { data, error } = await _sb.from('entries').update(row).eq('id', entry.id).select().single();
  if (error) throw new Error(error.message);
  return entryFromRow(data);
}

export async function deleteEntry(id) {
  await _sb.from('entries').delete().eq('id', id);
}

/** Re-insert a previously deleted entry (used by undo). No-op if already present. */
export async function restoreEntry(entry) {
  const { count } = await _sb
    .from('entries')
    .select('*', { count: 'exact', head: true })
    .eq('id', entry.id);
  if (count > 0) return;
  const row = entryToRow(entry, entry.userName);
  await _sb.from('entries').insert(row);
}

// ── Projects ──────────────────────────────────────────────────────────────────

export async function getProjects() {
  const { data } = await _sb.from('projects').select('*').order('name');
  return data || [];
}

export async function saveProject(project) {
  const fields = { name: project.name, active: project.active, billable: project.billable };
  if (!project.id) {
    const { data } = await _sb.from('projects').insert(fields).select().single();
    return data;
  }
  const { data } = await _sb.from('projects').update(fields).eq('id', project.id).select().single();
  return data;
}

export async function deleteProject(id) {
  await _sb.from('projects').delete().eq('id', id);
}

export async function reassignProjectEntries(fromId, toId) {
  await _sb.from('entries')
    .update({ project_id: toId || null })
    .eq('project_id', fromId);
}

// ── Categories ────────────────────────────────────────────────────────────────

export async function getCategories() {
  const { data } = await _sb.from('categories').select('*').order('name');
  return data || [];
}

export async function saveCategory(category) {
  const fields = { name: category.name, color: category.color, type: category.type };
  if (!category.id) {
    const { data } = await _sb.from('categories').insert(fields).select().single();
    return data;
  }
  const { data } = await _sb.from('categories').update(fields).eq('id', category.id).select().single();
  return data;
}

export async function deleteCategory(id) {
  const { count } = await _sb
    .from('entries')
    .select('*', { count: 'exact', head: true })
    .eq('category_id', id);
  if (count > 0) throw new Error('Cannot delete: this category has existing time entries.');
  await _sb.from('categories').delete().eq('id', id);
}
